import config from "environment";
import { getRequest, postRequest } from "./client";
import {
  LinkRecordFull,
  CreateCollectionWithEverythingContract,
  UpdateCollectionWithEverythingContract,
  EmbedCollectionResult,
  Snippet,
  AIResponseSave,
  AddCollectionResourcesContract,
  AddGoogleDriveResourcesToCollectionsContract,
  Collection,
  CollectionFavorite,
  AddOneDriveResourcesToCollectionContract,
  AddSharePointResourcesToCollectionContract,
  AddConfluenceResourcesToCollectionContract,
  LinkRecordAction,
} from "winslow-core";
import {
  CollectionQuestionCount,
  FavoriteCollectionForUserPayload,
  UnfavoriteCollectionForUserPayload,
  UpdateCollectionAccessTypePayload,
} from "./CollectionService.types";

const { baseApi } = config;

export async function apiGetCollection(
  collectionId: string
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/collection/${collectionId}`;
  return await getRequest(fetchURL, "API/apiGetCollection");
}

export async function apiCreateCollectionWithEverything(
  payload: CreateCollectionWithEverythingContract["Body"]
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/create-with-everything`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiCreateCollectionWithEverything"
  );
}

export async function apiUpdateCollectionWithEverything(
  payload: UpdateCollectionWithEverythingContract["Body"]
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/update-with-everything`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiUpdateCollectionWithEverything"
  );
}

export async function apiAddCollectionResources(
  payload: AddCollectionResourcesContract["Body"]
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/add-collection-resources`;
  return await postRequest(fetchURL, payload, "API/apiAddCollectionResources");
}

export async function apiAddGoogleDriveResourcesToCollection(
  payload: AddGoogleDriveResourcesToCollectionsContract["Body"]
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/add-google-drive-resources-to-collection`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiAddGoogleDriveResourcesToCollection"
  );
}

export async function apiAddOneDriveResourcesToCollection(
  payload: AddOneDriveResourcesToCollectionContract["Body"]
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/add-ms-drive-resources-to-collection`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiAddMSDriveResourcesToCollection"
  );
}

export async function apiAddSharePointResourcesToCollection(
  payload: AddSharePointResourcesToCollectionContract["Body"]
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/add-sharepoint-resources-to-collection`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiAddSharePointResourcesToCollection"
  );
}

export async function apiAddConfluenceResourcesToCollection(
  payload: AddConfluenceResourcesToCollectionContract["Body"]
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/add-confluence-resources-to-collection`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiAddConfluenceResourcesToCollection"
  );
}

// favorite a colleciton for a user
export async function apiFavoriteCollectionForUser(
  payload: FavoriteCollectionForUserPayload
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/favorite`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiFavoriteCollectionForUser"
  );
}

// unfavorite a colleciton for a user
export async function apiUnfavoriteCollectionForUser(
  payload: UnfavoriteCollectionForUserPayload
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/unfavorite`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiUnfavoriteCollectionForUser"
  );
}

// get link records for the colleciton
export async function apiGetCollectionLinkRecords(
  collectionId: string
): Promise<LinkRecordFull[] | null> {
  const fetchURL = `${baseApi}/collections/link-records/${collectionId}`;
  return await getRequest(fetchURL, "API/apiGetCollectionLinkRecords");
}

export async function apiGetCollectionSnippets(
  collectionId: string
): Promise<Snippet[] | null> {
  const fetchURL = `${baseApi}/collections/snippets/${collectionId}`;
  return await getRequest(fetchURL, "API/apiGetCollectionSnippets");
}

export async function apiEmbedCollection(collectionId: string) {
  const fetchURL = `${baseApi}/ai/embed-collection/${collectionId}`;
  return await getRequest(fetchURL, "API/apiEmbedCollection");
}

export async function apiResyncCollection(collectionId: string) {
  const fetchURL = `${baseApi}/ai/resync-collection/${collectionId}`;
  return await getRequest<EmbedCollectionResult>(
    fetchURL,
    "APIapiResyncCollection"
  );
}

export async function apiGetCollectionQuestionCountForUserInWorkspace(
  workspaceId: string,
  userId: string
): Promise<CollectionQuestionCount[] | null> {
  const fetchURL = `${baseApi}/collections/question-count/${workspaceId}/user/${userId}`;
  return await getRequest(
    fetchURL,
    "apiGetCollectionQuestionCountForUserInWorkspace"
  );
}

export async function apiUpdateCollectionAccessType(
  payload: UpdateCollectionAccessTypePayload
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/update-access`;
  return await postRequest(
    fetchURL,
    payload,
    "API/apiUpdateCollectionAccessType"
  );
}

export async function apiDeleteCollection(
  collectionId: string
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/delete/${collectionId}`;
  return await getRequest(fetchURL, "apiDeleteCollection");
}

export async function apiDeleteCollectionFolderAndResources(
  collectionId: string,
  folderRootId: string
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/delete-folder/${collectionId}/${folderRootId}`;
  return await getRequest(fetchURL, "apiDeleteCollectionFolderAndResources");
}

export async function apiDeleteAllCollectionResources(
  collectionId: string
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/delete-all-resources/${collectionId}`;
  return await getRequest(fetchURL, "API/apiDeleteAllCollectionResources");
}

export async function apiUpdateCollectionCompanyInfo(
  collectionId: string,
  companyName: string | null,
  companyPrompt: string | null
): Promise<Collection | null> {
  const fetchURL = `${baseApi}/collections/update-company-info`;
  return await postRequest(
    fetchURL,
    {
      collectionId,
      companyName,
      companyPrompt,
    },
    "API/apiUpdateCollectionCompanyInfo"
  );
}

export async function apiGetCollectionLinkRecordActions(
  collectionId: string
): Promise<LinkRecordAction[] | null> {
  const fetchURL = `${baseApi}/collections/actions/${collectionId}`;
  return await getRequest(fetchURL, "API/apiGetCollectionLinkRecordActions");
}

export async function apiGetLinkRecordActions(
  linkRecordId: string
): Promise<LinkRecordAction[] | null> {
  const fetchURL = `${baseApi}/collections/link-record-actions/${linkRecordId}`;
  return await getRequest(fetchURL, "API/apiGetLinkRecordActions");
}
